/**
 * Signature re-capture page (PF-6119)
 *
 * Route: `/signature-recapture/:recaptureId`. Standalone, generic and permanently deployed — who can
 * complete it is decided server-side by the `appInfo/signatureRecapture` allowlist, not by this
 * route, so the page being public is harmless.
 *
 * The link's status is resolved BEFORE anything else is shown. A used or unknown link gets a notice
 * and no phone form: `loadOpenTarget` rejects a completed target exactly like a wrong number, so a
 * patient tapping their link a second time used to be told their phone number did not match. The
 * status check is also what makes the link genuinely single-use from the patient's side rather than
 * only server-side.
 *
 * State machine: `checking → (completed | invalid) | phone → sign → done`. Nothing is persisted, so
 * a reload re-checks the status and returns to the phone step — correct, since the session lives
 * server-side and the phone step is cheap to repeat.
 */
import type { SignatureRecaptureLinkStatus, VerifySignatureRecapturePhoneResponse } from '@physiofit-digital/physiofit_types';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getSignatureRecaptureStatus } from './api/getSignatureRecaptureStatus';
import { signatureRecaptureCopy } from './config/copy';
import { LinkNoticeScreen } from './screens/LinkNoticeScreen';
import { PhoneScreen } from './screens/PhoneScreen';
import { SignScreen } from './screens/SignScreen';
import { CheckingLinkScreen } from './screens/CheckingLinkScreen';
import { SuccessScreen } from './screens/SuccessScreen';

type RecaptureScreenName = 'checking' | 'phone' | 'sign' | 'done';

/** Long enough for a slow mobile connection, short enough that nobody sits staring at a spinner. */
const STATUS_TIMEOUT_MS = 12_000;

const withTimeout = <T,>(promise: Promise<T>): Promise<T> =>
  Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Status check timed out')), STATUS_TIMEOUT_MS),
    ),
  ]);

export function SignatureRecapturePage() {
  const { recaptureId: rawRecaptureId } = useParams<{ recaptureId: string }>();
  const recaptureId = rawRecaptureId?.trim() ?? '';

  const [screen, setScreen] = useState<RecaptureScreenName>('checking');
  const [linkStatus, setLinkStatus] = useState<SignatureRecaptureLinkStatus | null>(null);
  const [verified, setVerified] = useState<VerifySignatureRecapturePhoneResponse | null>(null);

  useEffect(() => {
    // A missing id needs no round trip and no state: it is derived during render below. Setting it
    // here would be a cascading render for something already known.
    if (!recaptureId) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        // A callable that never settles leaves the patient on a spinner forever — seen on a phone,
        // where the request can be swallowed rather than refused. The SDK's own deadline is far too
        // long to be a safety net for someone holding a phone, so bound it here.
        const { status } = await withTimeout(getSignatureRecaptureStatus({ recaptureId }));
        if (cancelled) {
          return;
        }
        setLinkStatus(status);
        if (status === 'open') {
          setScreen('phone');
        }
      } catch {
        // An unreachable status check must not open the phone form: it cannot tell an exhausted link
        // from a healthy one, and guessing "open" is the guess that wastes the patient's time.
        if (!cancelled) {
          setLinkStatus('invalid');
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [recaptureId]);

  if (linkStatus === 'completed') {
    return <LinkNoticeScreen {...signatureRecaptureCopy.alreadyCompleted} />;
  }

  // `!recaptureId` is the route carrying no id at all — indistinguishable to the patient from an id
  // we do not know, and deliberately so.
  if (linkStatus === 'invalid' || !recaptureId) {
    return <LinkNoticeScreen {...signatureRecaptureCopy.invalidLink} />;
  }

  if (screen === 'checking') {
    return <CheckingLinkScreen />;
  }

  if (screen === 'sign' && verified) {
    return (
      <SignScreen
        sessionToken={verified.sessionToken}
        documentUrl={verified.documentUrl}
        documentName={verified.documentName}
        thresholds={verified.thresholds}
        onBack={() => setScreen('phone')}
        onSubmitted={() => setScreen('done')}
      />
    );
  }

  if (screen === 'done' && verified) {
    return <SuccessScreen firstName={verified.patientFirstName} />;
  }

  return (
    <PhoneScreen
      recaptureId={recaptureId}
      onVerified={(result) => {
        setVerified(result);
        setScreen('sign');
      }}
    />
  );
}
